import React from 'react';
import classNames from 'classnames';
import propTypes from 'prop-types';
import { get } from 'lodash';

// components
import { Card } from '@tcc/crate';
import NumZones from './NumZones';
import Prices from './Prices';
import BrandAndHybrid from './BrandAndHybrid';
import TargetYield from './TargetYield';
import PlantingDate from './PlantingDate';
import Season from './Season';
import CurrentAvgPopulation from './CurrentAvgPopulation';

const baseString = 'vra-planting-plan-form';
const AdvancedPlantingPlanForm = ({
  accountCountry,
  bagSize,
  canViewYieldData,
  currency,
  isReviewScriptStep,
  maxDate,
  maxZoneCount,
  minDate,
  minZoneCount,
  onChangeHybridSearchQuery,
  onChangeNumZones,
  onChangePriceCommodity,
  onChangePriceProduct,
  onChangeTargetYield,
  onSelectDate,
  onSelectProduct,
  numZonesInActiveScenario,
  plantingPlanParams,
  seedProducts,
  validationResult,
  yieldMapsHistoricalData,
  onSelectSeason,
  fieldCountry,
  isSingleZoneRecommendationEnabled,
  onChangeCurrentAvgPopulation
}) => {
  const {
    productId:currentProductId,
    'price-commodity': priceCommodity,
    'price-product': priceProduct,
    'target-yield': targetYield,
    currentAveragePopulation
  } = plantingPlanParams;

  const classString = classNames({
    [`${baseString}`]: true,
    [`${baseString}--mini`]: isReviewScriptStep,
    [`${baseString}--density-tool`]: isReviewScriptStep
  });

  const spacerClassString = 'vra-planting-plan-form__spacer';
  const dividerClassString = 'vra-planting-plan-form__divider';
  const form = (
    <form>
      <PlantingDate
        isReviewScriptStep={isReviewScriptStep}
        maxDate={maxDate}
        minDate={minDate}
        onSelectDate={onSelectDate}
        selectedDate={plantingPlanParams.date}
        validationResult={validationResult}
      />
      <Season 
        fieldCountry={fieldCountry}
        isReviewScriptStep={isReviewScriptStep}
        selectedSeasonId={plantingPlanParams.agronomicSeason}
        onSelectSeason={onSelectSeason}
      />
      <BrandAndHybrid
        currentProductId={currentProductId}
        isReviewScriptStep={isReviewScriptStep}
        onChangeSearchQuery={onChangeHybridSearchQuery}
        onSelectProduct={onSelectProduct}
        seedProducts={seedProducts}
        validationResult={validationResult}
      />
      {
        !isReviewScriptStep
        && <CurrentAvgPopulation
          accountCountry={accountCountry}
          isReviewScriptStep={isReviewScriptStep}
          onChangeCurrentAvgPopulation={onChangeCurrentAvgPopulation}
          population={currentAveragePopulation}
          validationResult={get(validationResult, 'resultsByKey.currentAveragePopulation')}
        />
      }
      <TargetYield
        canViewYieldData={canViewYieldData}
        isReviewScriptStep={isReviewScriptStep}
        onChangeTargetYield={onChangeTargetYield}
        plantingPlanParams={plantingPlanParams}
        targetYield={targetYield}
        validationResult={get(validationResult, 'resultsByKey.targetYield')}
        yieldMapsHistoricalData={yieldMapsHistoricalData}
      />
      <div className={dividerClassString}/>
      <Prices
        accountCountry={accountCountry}
        bagSize={bagSize}
        currency={currency}
        isReviewScriptStep={isReviewScriptStep}
        onChangePriceCommodity={(price) => onChangePriceCommodity(price, currency)}
        onChangePriceProduct={(price) => onChangePriceProduct(price, currency)}
        plantingPlanParams={plantingPlanParams}
        priceCommodity={priceCommodity}
        priceProduct={priceProduct}
        validationResult={validationResult}
      />
      <div className={dividerClassString}/>
      <NumZones
        isReviewScriptStep={isReviewScriptStep}
        maxZoneCount={maxZoneCount}
        minZoneCount={minZoneCount}
        numZonesInActiveScenario={numZonesInActiveScenario}
        onChangeNumZones={onChangeNumZones}
        plantingPlanParams={plantingPlanParams}
        isSingleZoneRecommendationEnabled={isSingleZoneRecommendationEnabled}
      />
      <div className={spacerClassString}/>
    </form>
  );


  return (
    isReviewScriptStep
      ? (
        <section className={classString}>
          {form}
        </section>
      )
      : (
        <Card className={classString}>
          {form}
        </Card>
      )
  );
};

AdvancedPlantingPlanForm.propTypes = {
  accountCountry: propTypes.string.isRequired,
  bagSize: propTypes.number.isRequired,
  canViewYieldData: propTypes.bool.isRequired,
  currency: propTypes.string.isRequired,
  isReviewScriptStep: propTypes.bool.isRequired,
  maxDate: propTypes.string.isRequired,
  maxZoneCount: propTypes.number.isRequired,
  minDate: propTypes.string.isRequired,
  minZoneCount: propTypes.number.isRequired,
  numZonesInActiveScenario: propTypes.number,
  onChangeGoalRatio: propTypes.func.isRequired,
  onChangeHybridSearchQuery: propTypes.func.isRequired,
  onChangeNumZones: propTypes.func.isRequired,
  onChangePriceCommodity: propTypes.func,
  onChangePriceProduct: propTypes.func,
  onChangeTargetYield: propTypes.func,
  onSelectDate: propTypes.func.isRequired,
  onSelectProduct: propTypes.func.isRequired,
  plantingPlanParams: propTypes.object.isRequired,
  seedProducts: propTypes.array.isRequired,
  validationResult: propTypes.object,
  yieldMapsHistoricalData: propTypes.object.isRequired,
  onSelectSeason: propTypes.func.isRequired,
  fieldCountry: propTypes.string.isRequired,
  isSingleZoneRecommendationEnabled: propTypes.bool.isRequired,
  onChangeCurrentAvgPopulation: propTypes.func.isRequired
};

export default AdvancedPlantingPlanForm;
